import { useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";

export const Lightbox = ({ images = [], index = 0, onClose, onNav }) => {
  const open = index !== null && index >= 0;

  const handleKey = useCallback(
    (e) => {
      if (!open) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") onNav((index + 1) % images.length);
      if (e.key === "ArrowLeft") onNav((index - 1 + images.length) % images.length);
    },
    [open, index, images.length, onClose, onNav]
  );

  useEffect(() => {
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [handleKey]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[100] bg-ink/95 backdrop-blur-sm flex items-center justify-center"
          onClick={onClose}
          data-testid="lightbox"
        >
          <button
            data-testid="lightbox-close"
            onClick={onClose}
            className="absolute top-6 right-6 text-sand-bg/80 hover:text-sand-bg transition-colors z-10"
          >
            <X className="w-7 h-7" strokeWidth={1.2} />
          </button>

          {images.length > 1 && (
            <>
              <button
                data-testid="lightbox-prev"
                onClick={(e) => { e.stopPropagation(); onNav((index - 1 + images.length) % images.length); }}
                className="absolute left-4 md:left-8 text-sand-bg/70 hover:text-sand-bg transition-colors"
              >
                <ChevronLeft className="w-9 h-9" strokeWidth={1} />
              </button>
              <button
                data-testid="lightbox-next"
                onClick={(e) => { e.stopPropagation(); onNav((index + 1) % images.length); }}
                className="absolute right-4 md:right-8 text-sand-bg/70 hover:text-sand-bg transition-colors"
              >
                <ChevronRight className="w-9 h-9" strokeWidth={1} />
              </button>
            </>
          )}

          <motion.img
            key={index}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: [0.19, 1, 0.22, 1] }}
            src={images[index]}
            alt="Villa Cala Rossa"
            onClick={(e) => e.stopPropagation()}
            className="max-h-[85vh] max-w-[90vw] object-contain shadow-2xl"
          />

          <span className="absolute bottom-6 text-sand-bg/60 text-xs tracking-widest">
            {index + 1} / {images.length}
          </span>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
